import { useIntl } from 'react-intl';
import { createColumnHelper } from '@tanstack/react-table';
import { PriceChange } from 'pages/trading/model';
import { useModelService } from 'services/model/model.service';

import { useDataEntriesInTables } from 'components/table/useDataEntriesInTables';
import { usePromotionMeasures } from 'pages/trading/price/promotion/promotionMeasures';
import { formatValueAsPositiveOrNegative } from 'components/table/DataTableUtil';

export interface PromotionTableRow {
  id: string;
  artImageUrl: string;
  prdName: string;
  styBrandColor: string;
  chnName: string;
  oldPrice: number;
  promoPrice?: number;
  newPrice: number;
  priceChangeDelta: number;
  dailyGmv: number;
  dailyGmvDelta: number;
  oldDailyQty: number;
  dailyQty: number;
  dailyQtyDelta: number;
  originalData: PriceChange;
}

export const usePromotionTableColumns = () => {
  const intl = useIntl();

  const promoMeasureProvider = usePromotionMeasures();

  const columnHelper = createColumnHelper<PromotionTableRow>();
  const { getAttributes, getMeasures } = useModelService(promoMeasureProvider);

  const [oldPrice, promoPrice, newPrice, oldDailyQty, dailyQty, dailyQtyDelta, dailyGmvDelta] = getMeasures([
    'oldPrice',
    'promoPrice',
    'newPrice',
    'oldDailyQty',
    'dailyQty',
    'dailyQtyDelta',
    'dailyGmvDelta',
  ]);

  const [prdName, chnName, artImageUrl, styBrandColor] = getAttributes([
    'prdName',
    'chnName',
    'artImageUrl',
    'styBrandColor',
  ]);

  const { createAttributeColumn, createMeasureColumn } = useDataEntriesInTables();

  return [
    createAttributeColumn(artImageUrl),
    createAttributeColumn(prdName),
    createAttributeColumn(styBrandColor),
    createAttributeColumn(chnName),

    columnHelper.group({
      id: 'Price',
      header: intl.formatMessage({ id: 'trading.price_change.table.price_header' }),
      meta: { skipExport: true },
      columns: [
        createMeasureColumn(oldPrice, {
          meta: {
            sortTitleId: 'trading.price_change.table.price_header',
            sortSubtitleId: 'trading.promotion.table.before.promotion',
          },
        }),
        createMeasureColumn(promoPrice, {
          meta: {
            sortTitleId: 'trading.price_change.table.price_header',
            sortSubtitleId: 'trading.promotion.table.during.promotion',
          },
        }),
        createMeasureColumn(newPrice, {
          meta: {
            sortTitleId: 'trading.price_change.table.price_header',
            sortSubtitleId: 'trading.promotion.table.after.promotion',
          },
        }),
      ],
    }),
    columnHelper.group({
      id: 'ItemsPerDay',
      header: intl.formatMessage({ id: 'trading.price_change.table.daily_items' }),
      meta: { skipExport: true },
      columns: [
        createMeasureColumn(oldDailyQty, {
          meta: {
            sortTitleId: 'trading.price_change.table.daily_items',
            sortSubtitleId: 'trading.promotion.table.before.promotion',
          },
        }),
        createMeasureColumn(dailyQty, {
          meta: {
            sortTitleId: 'trading.price_change.table.daily_items',
            sortSubtitleId: 'trading.promotion.table.after.promotion',
          },
        }),
        createMeasureColumn(dailyQtyDelta, {
          className: row => formatValueAsPositiveOrNegative(row.getValue(dailyQtyDelta.code)),
          signedValue: true,
        }),
      ],
    }),

    createMeasureColumn(dailyGmvDelta, {
      className: row => formatValueAsPositiveOrNegative(row.getValue(dailyGmvDelta.code)),
      header: { subHeaderId: 'trading.promotion.table.after.promotion' },
      signedValue: true,
    }),
  ];
};
